import React from 'react';
import { useTranslation } from 'react-i18next';

import SiteApi from '../../Api/SiteApi/SiteApi';
import LoadThemeComponent from '../../base/LoadThemeComponent';
import {
    formatDateString,
    formatDateStringShort,
    formatLongWithTimezone,
    getDetailedRelativeTime,
    notificationIconType,
} from '../../utils/formatters';

import { DatabaseNotification } from './types';

type Props = {
    notification: DatabaseNotification;
    showFull?: boolean;
    updateList?: Function;
};

function NotificationItem({ notification, showFull, updateList }: Props) {
    const { t } = useTranslation();
    const { days, hours, minutes } = getDetailedRelativeTime(notification?.created_at);

    const getRelativeTimeText = () => {
        if (days > 0) {
            return `${days} ${t('counter.days')} ${hours} ${t('counter.hours')}`;
        }
        if (hours > 0) {
            return `${hours} ${t('counter.hours')} ${minutes} ${t('counter.minutes')}`;
        }
        return `${minutes} ${t('counter.minutes')}`;
    };

    const getNotificationText = () => {
        if (!notification?.data?.extra?.short_text_id) {
            const date = notification.data.date;
            const dateString = date ? formatDateString(date) : '';
            return `${notification.data.extra.short_text} ${dateString}`;
        }

        const variables = {};
        notification.data.extra.short_text_variables?.forEach((variable) => {
            const key = variable.key.replace(/^{{|}}$/g, ''); // backward compatibility - may be removed in the future
            if (key === 'date') {
                variables[key] = formatLongWithTimezone(variable.value);
                return;
            }
            variables[key] = variable.value;
        });

        return t(notification.data.extra.short_text_id, variables);
    };

    const handleNotificationDelete = () => {
        SiteApi.delete(`notifications/remove/${notification.id}`).then((response) => {
            if (response.status === 200) {
                updateList();
            }
        });
    };

    const baseView = () => {
        return showFull ? (
            <div className="mt-4">
                <time
                    dateTime={formatDateStringShort(notification.created_at)}
                    className={`card-subtitle ${!notification.read_at && 'font-weight-bold'}`}
                >
                    {formatLongWithTimezone(notification.created_at)}
                </time>
                <button
                    onClick={handleNotificationDelete}
                    className="d-inline bg-transparent m-0 border-0 p-0"
                    type="submit"
                >
                    <i className="fas fa-trash-alt text-danger ml-1" />
                </button>
                <div className="item-container d-flex mx-auto">
                    <div className="icon-box">
                        <i className={`mr-3 mt-1 icon fas ${notificationIconType(notification.data.extra.type)}`} />
                    </div>
                    <a href="#" onClick={(e) => markNotificationAsRead(notification, e)}>
                        {' '}
                        {getNotificationText()}
                    </a>
                </div>
            </div>
        ) : (
            <div className="dropdown-item">
                <a href="#" className="notificationLink" onClick={(e) => markNotificationAsRead(notification, e)}>
                    <div className="item-container d-flex mx-auto">
                        <div className="icon-box">
                            <i className={`mr-3 icon fas ${notificationIconType(notification.data.extra.type)}`} />
                        </div>
                        <p> {getNotificationText()}</p>
                    </div>
                </a>
            </div>
        );
    };

    const defaultView = () => (
        <div className="dropdown-item">
            <a href="#" className="notificationLink" onClick={(e) => markNotificationAsRead(notification, e)}>
                <div className="item-container d-flex flex-column mx-auto">
                    <div className="notification-container d-flex">
                        <div className="bullet-box">
                            <span className="bullet" />
                        </div>
                        <p> {getNotificationText()}</p>
                    </div>
                    <div className="notification-data-container">
                        <span className="notification-data">{getRelativeTimeText()}</span>
                    </div>
                </div>
            </a>
        </div>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}

async function markNotificationAsRead(
    notification: DatabaseNotification,
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
) {
    e.preventDefault();
    try {
        if (!notification.read_at) {
            await SiteApi.post('notifications/mark', { notificationId: notification.id });
        }
        window.location.href = notification.data.extra.route;
    } catch (error) {
        // NOOP
    }
}

export default NotificationItem;
